import { createStyles, Group } from '@mantine/core';
import { IconButton, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { usePropertyStyles } from '../styles';

const useStyles = createStyles(() => ({
	wrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		minWidth: 0,
	},
	icon: {
		flexShrink: 0,
	},
}));

export interface ISelectablePropertyProps {
	wideLabel?: boolean;
	label?: string;
	labelTooltip?: string;
	customTooltip?: ReactNode;
	wrapValueWith?: (children: ReactNode) => ReactNode;
	children: ReactNode;
	dataTestId?: string;
}

function SelectablePropertyInternal(
	{
		wideLabel,
		label,
		labelTooltip,
		customTooltip,
		wrapValueWith,
		children,
		dataTestId,
	}: ISelectablePropertyProps,
	ref: ForwardedRef<HTMLDivElement>
) {
	const { classes, theme, cx } = usePropertyStyles();
	const { classes: customTooltipClasses } = useStyles();

	const rightColContent = (
		<Group
			className={classes.rightCol}
			data-testid={`selectable-property-${dataTestId}`}
		>
			{children}
		</Group>
	);

	return (
		<Group spacing={0} noWrap ref={ref}>
			{label && (
				<Group
					className={cx(classes.leftCol, {
						[classes.leftColWide]: wideLabel,
					})}
				>
					<div className={customTooltipClasses.wrapper}>
						<Text
							className={classes.label}
							style={{
								flex: 1,
								minWidth: 0,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{label}
						</Text>
						{customTooltip && (
							<div className={customTooltipClasses.icon}>{customTooltip}</div>
						)}
						{labelTooltip && (
							<IconButton
								iconName="infoCircle"
								tooltip={labelTooltip}
								variant="tertiary"
								tooltipProps={{
									withinPortal: true,
									w: theme.other.space[90],
									multiline: true,
								}}
								className={customTooltipClasses.icon}
							/>
						)}
					</div>
				</Group>
			)}
			{wrapValueWith ? wrapValueWith(rightColContent) : rightColContent}
		</Group>
	);
}

export const SelectableProperty = observer(
	forwardRef(SelectablePropertyInternal)
);
